import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { parseISO, sub, isBefore } from 'date-fns';
import { useAppAuth } from '../auth/appAuth';
import { useFilterSetData } from '../hooks/useFilterSetData';
import { useAxiosClient } from '../utils/useAxiosClient';
import { fetchBrandColors, getBrandColors } from '../utils/brandColors';
import { fetchPillarColors, getPillarColors } from '../utils/pillarColors';

// Development auth bypass
const DEV_BYPASS_AUTH = process.env.REACT_APP_BYPASS_AUTH === 'true';

// Define the type for our filter context
export interface FilterContextType {
  // Filter states
  selectedBrands: string[];
  selectedPlatforms: string[];
  selectedPillars: string[];
  startDate: Date | null;
  endDate: Date | null;
  
  // Filter update methods
  setSelectedBrands: (brands: string[]) => void;
  setSelectedPlatforms: (platforms: string[]) => void;
  setSelectedPillars: (pillars: string[]) => void;
  setStartDate: (date: Date | null) => void;
  setEndDate: (date: Date | null) => void;
  
  // Utility
  updateUrlParams: (params: Record<string, string | string[] | undefined>) => void;
  
  // Override control
  useDefaultFilters: boolean;
  setUseDefaultFilters: (use: boolean) => void;

  // Initialization state
  isInitialized: boolean;
  
  // Organization data
  orgName: string | null;
  availableBrands: { id: string; name: string }[];
  allBrands: string[];
  availablePlatforms: { id: string; name: string }[];
  availablePillars: string[];
  dataDateRange: { start: Date | null; end: Date | null };
  dashboardType: string | null;
  
  // DEI target pillar
  deiTargetPillar: string | null;
  
  // Brand colors
  brandColors: { [key: string]: string };
  brandColorsLoading: boolean;
  refreshBrandColors: () => void;
  
  // Pillar colors
  pillarColors: { [key: string]: string };
  pillarColorsLoading: boolean;
  refreshPillarColors: () => void;
}

// Create the context with an undefined initial value
export const FilterContext = createContext<FilterContextType | undefined>(undefined);

export const useFilterContext = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilterContext must be used within a FilterContextProvider');
  }
  return context;
};

interface FilterContextProviderProps {
  children: ReactNode;
}

export const FilterContextProvider: React.FC<FilterContextProviderProps> = ({ children }) => {
  const { orgId: orgIdStr } = useParams<{ orgId: string }>();
  const orgId = parseInt(orgIdStr || '0', 10);
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAppAuth();
  const axiosClient = useAxiosClient();
  
  // Track token availability
  const [hasToken, setHasToken] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize states first
  const [selectedBrands, setSelectedBrandsState] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatformsState] = useState<string[]>([]);
  const [selectedPillars, setSelectedPillarsState] = useState<string[]>([]);
  const [startDate, setStartDateState] = useState<Date | null>(null);
  const [endDate, setEndDateState] = useState<Date | null>(null);
  const [useDefaultFilters, setUseDefaultFilters] = useState(true);

  // Brand colors state
  const [brandColors, setBrandColors] = useState<{ [key: string]: string }>({});
  const [brandColorsLoading, setBrandColorsLoading] = useState(true);
  const [colorRefreshKey, setColorRefreshKey] = useState(0);

  // Pillar colors state
  const [pillarColors, setPillarColors] = useState<{ [key: string]: string }>({});
  const [pillarColorsLoading, setPillarColorsLoading] = useState(true);
  const [pillarRefreshKey, setPillarRefreshKey] = useState(0);

  // Get token on mount and when auth state changes
  useEffect(() => {
    const getToken = async () => {
      // Dev bypass: skip token requirement
      if (DEV_BYPASS_AUTH) {
        console.log('🔓 FilterContext: Auth bypass enabled');
        setHasToken(true);
        return;
      }
      
      if (isAuthenticated && !authLoading) {
        try {
          await getAccessTokenSilently();
          setHasToken(true);
        } catch (error) {
          console.error('Error getting access token:', error);
          setHasToken(false);
        }
      } else {
        setHasToken(false);
      }
    };

    getToken();
  }, [isAuthenticated, authLoading, getAccessTokenSilently]);

  // Fetch filter set data (combined org metadata, brands, platforms, pillars)
  const { 
    filterSetData
    // filterSetLoading, // Removed as unused
    // filterSetError, // Removed as unused
  } = useFilterSetData(orgId, { 
    enabled: hasToken && !!orgId 
  });

  // Memoize availablePillarsFromFilterSet to stabilize its reference for dependent callbacks and context value
  const availablePillarsFromFilterSet = React.useMemo(() => {
    // Ensure we always return an array
    return filterSetData?.pillars?.pillars || [];
  }, [filterSetData]);

  // Derive allBrands (string array) from filterSetData.brands (array of objects)
  const allBrandsList = React.useMemo(() => {
    return filterSetData?.brands?.map(brand => brand.name) || [];
  }, [filterSetData]);

  // Brand colors loading function
  const loadBrandColors = useCallback(async () => {
    if (!axiosClient || !filterSetData?.brands || filterSetData.brands.length === 0) {
      return;
    }
    
    try {
      setBrandColorsLoading(true);
      const brandColorConfig = await fetchBrandColors(axiosClient);
      const brandNames = filterSetData.brands.map(brand => brand.name);
      const colors = await getBrandColors(axiosClient, brandNames, brandNames, brandColorConfig);
      setBrandColors(colors as { [key: string]: string });
    } catch (error) {
      console.error('Error loading brand colors in FilterContext:', error);
      // Set fallback colors
      const fallbackColors: { [key: string]: string } = {};
      filterSetData.brands.forEach((brand, index) => {
        fallbackColors[brand.name] = `hsl(${index * 45}, 70%, 50%)`;
      });
      setBrandColors(fallbackColors);
    } finally {
      setBrandColorsLoading(false);
    }
  }, [axiosClient, filterSetData, colorRefreshKey]);

  // Refresh brand colors function
  const refreshBrandColors = useCallback(() => {
    setColorRefreshKey(prev => prev + 1);
  }, []);

  // Load brand colors when filter data is available
  useEffect(() => {
    if (hasToken && filterSetData?.brands) {
      loadBrandColors();
    }
  }, [hasToken, filterSetData, loadBrandColors]);

  // Listen for brand color updates from settings modal
  useEffect(() => {
    const handleBrandColorsUpdated = () => {
      refreshBrandColors();
    };

    window.addEventListener('brandColorsUpdated', handleBrandColorsUpdated);
    return () => window.removeEventListener('brandColorsUpdated', handleBrandColorsUpdated);
  }, [refreshBrandColors]);

  // Pillar colors loading function
  const loadPillarColors = useCallback(async () => {
    if (!axiosClient || !filterSetData?.pillars?.pillars || filterSetData.pillars.pillars.length === 0) {
      return;
    }
    
    try {
      setPillarColorsLoading(true);
      const pillarColorConfig = await fetchPillarColors(axiosClient);
      const pillarNames = filterSetData.pillars.pillars;
      const colors = await getPillarColors(axiosClient, pillarNames, pillarNames, pillarColorConfig);
      setPillarColors(colors as { [key: string]: string });
    } catch (error) {
      console.error('Error loading pillar colors in FilterContext:', error);
      // Set fallback colors
      const fallbackColors: { [key: string]: string } = {};
      filterSetData.pillars.pillars.forEach((pillar, index) => {
        fallbackColors[pillar] = `hsl(${index * 45}, 70%, 50%)`;
      });
      setPillarColors(fallbackColors);
    } finally {
      setPillarColorsLoading(false);
    }
  }, [axiosClient, filterSetData, pillarRefreshKey]);

  // Refresh pillar colors function
  const refreshPillarColors = useCallback(() => {
    setPillarRefreshKey(prev => prev + 1);
  }, []);

  // Load pillar colors when filter data is available
  useEffect(() => {
    if (hasToken && filterSetData?.pillars) {
      loadPillarColors();
    }
  }, [hasToken, filterSetData, loadPillarColors]);

  // Listen for pillar color updates from settings modal
  useEffect(() => {
    const handlePillarColorsUpdated = () => {
      refreshPillarColors();
    };

    window.addEventListener('pillarColorsUpdated', handlePillarColorsUpdated);
    return () => window.removeEventListener('pillarColorsUpdated', handlePillarColorsUpdated);
  }, [refreshPillarColors]);

  // Utility function to update URL parameters (memoized)
  const updateUrlParams = useCallback((params: Record<string, string | string[] | undefined>) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(params).forEach(([key, value]) => {
      // Remove existing values for this key
      newParams.delete(key);
      
      // Skip if value is undefined
      if (value === undefined) return;
      
      // Add new values
      if (Array.isArray(value)) {
        if (value.length > 0) {
          if (key === 'pillars') {
            // Special handling for pillars to support Flask's getlist
            value.forEach(pillar => {
              if (pillar) newParams.append(key, pillar);
            });
          } else {
            // For other arrays, join with commas
            newParams.set(key, value.join(','));
          }
        }
      } else if (value) {
        newParams.set(key, value);
      }
    });
    
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]); // Depend on searchParams and setSearchParams

  // Create wrapper functions that update both state and URL parameters (memoized)
  const setSelectedBrands = useCallback((brands: string[]) => {
    setSelectedBrandsState(brands);
    updateUrlParams({ brands });
  }, [updateUrlParams]); // Depend on updateUrlParams

  const setSelectedPlatforms = useCallback((platforms: string[]) => {
    setSelectedPlatformsState(platforms);
    updateUrlParams({ platforms });
  }, [updateUrlParams]);

  const setSelectedPillars = useCallback((newlySelectedPillars: string[]) => {
    const filteredPillars = newlySelectedPillars.filter(p => p !== 'undefined');
    setSelectedPillarsState(filteredPillars);

    // Robust check if all available pillars are selected
    // Use filteredPillars for comparison here as well
    const allPillarsCurrentlySelected = Array.isArray(availablePillarsFromFilterSet) &&
                                      availablePillarsFromFilterSet.length > 0 &&
                                      filteredPillars.length === availablePillarsFromFilterSet.length &&
                                      // Ensure set equality regardless of order
                                      filteredPillars.every(p => availablePillarsFromFilterSet.includes(p)) &&
                                      availablePillarsFromFilterSet.every(p => filteredPillars.includes(p));

    if (allPillarsCurrentlySelected) {
      updateUrlParams({ pillars: undefined }); // Pass undefined to ensure removal by updateUrlParams
    } else {
      updateUrlParams({ pillars: filteredPillars });
    }
  }, [updateUrlParams, availablePillarsFromFilterSet]); // Dependency on memoized availablePillarsFromFilterSet is crucial

  const setStartDate = useCallback((date: Date | null) => {
    setStartDateState(date);
    // Use local date format instead of ISO to avoid timezone issues
    const formattedDate = date ? 
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` 
      : undefined;
    updateUrlParams({ start_date: formattedDate });
  }, [updateUrlParams]); // Depend on updateUrlParams

  const setEndDate = useCallback((date: Date | null) => {
    setEndDateState(date);
    // Use local date format instead of ISO to avoid timezone issues
    const formattedDate = date ? 
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` 
      : undefined;
    updateUrlParams({ end_date: formattedDate });
  }, [updateUrlParams]); // Depend on updateUrlParams

  // Initialize pillars from URL or filter set data
  React.useEffect(() => {
    if (filterSetData) {
      // Check URL first for pillars using getAll to support multiple values
      const pillarsFromUrl = searchParams.getAll('pillars').filter(p => p !== 'undefined'); // Filter "undefined"
      if (pillarsFromUrl && pillarsFromUrl.length > 0) {
        // Use the array of pillars directly from getAll
        setSelectedPillars(pillarsFromUrl);
      } else {
        // Check for comma-separated format for backward compatibility
        const pillarsParam = searchParams.get('pillars');
        if (pillarsParam) {
          const parsedPillars = pillarsParam.split(',').filter(p => p !== 'undefined'); // Filter "undefined"
          setSelectedPillars(parsedPillars);
        } else {
          // If no URL params, set all available pillars
          setSelectedPillars(filterSetData.pillars.pillars || []);
        }
      }
    }
  }, [filterSetData, searchParams, setSelectedPillars]);

  // Initialize dates from URL or filter set data
  React.useEffect(() => {
    if (filterSetData) {
      const startParam = searchParams.get('start_date');
      const endParam = searchParams.get('end_date');
      
      // Default start date: Either from URL, or 60 days before end date,
      // or org's data start date if within 60 days of end date
      const dataStartDate = parseISO(filterSetData.date_range.start_date);
      const dataEndDate = filterSetData.date_range.end_date
          && filterSetData.date_range.end_date.toLowerCase() !== 'ongoing'
        ? parseISO(filterSetData.date_range.end_date)
        : new Date();
      
      const sixtyDaysAgo = sub(dataEndDate, { days: 60 });
      const defaultStart = isBefore(sixtyDaysAgo, dataStartDate) ? dataStartDate : sixtyDaysAgo;
      
      // Create dates at noon to avoid timezone issues
      const createTimezoneSafeDate = (dateString: string) => {
        const [year, month, day] = dateString.split('-').map(Number);
        // Create date at noon in local timezone to avoid date shifting issues
        return new Date(year, month - 1, day, 12, 0, 0);
      };
      
      // Set start date from: URL param -> default calculated
      if (startParam) {
        setStartDate(createTimezoneSafeDate(startParam));
      } else {
        setStartDate(defaultStart);
      }
      
      // Set end date from: URL param -> org data end date -> today
      if (endParam) {
        setEndDate(createTimezoneSafeDate(endParam));
      } else {
        setEndDate(dataEndDate);
      }
    }
  }, [filterSetData, searchParams, setStartDate, setEndDate]);

  // Initialize selectedBrands from URL or filter set data
  React.useEffect(() => {
    if (filterSetData?.brands && filterSetData.brands.length > 0) {
      const brandsFromUrl = searchParams.get('brands')?.split(',');
      if (brandsFromUrl) {
        setSelectedBrands(brandsFromUrl);
      } else {
        // If no URL param, use all available brands
        setSelectedBrands(filterSetData.brands.map(brand => brand.id));
      }
    }
  }, [filterSetData, searchParams, setSelectedBrands]);

  // Initialize selectedPlatforms from URL or filter set data
  React.useEffect(() => {
    if (filterSetData?.platforms && filterSetData.platforms.length > 0) {
      const platformsFromUrl = searchParams.get('platforms')?.split(',');
      if (platformsFromUrl) {
        setSelectedPlatforms(platformsFromUrl);
      } else {
        // If no URL param, use all available platforms
        setSelectedPlatforms(filterSetData.platforms.map(platform => platform.id.toLowerCase()));
      }
    }
  }, [filterSetData, searchParams, setSelectedPlatforms]);

  // Set initialized after we have data and token
  useEffect(() => {
    if (hasToken && filterSetData) {
      setIsInitialized(true);
    } else {
      setIsInitialized(false);
    }
  }, [hasToken, filterSetData]);

  // Prepare data from filter set for the context
  const orgName = filterSetData?.org_name || null;
  const availableBrands = filterSetData?.brands || [];
  const availablePlatforms = filterSetData?.platforms || [];
  // Use the memoized version for the context value
  const availablePillars = availablePillarsFromFilterSet;
  const dataDateRange = {
    start: filterSetData?.date_range?.start_date ? parseISO(filterSetData.date_range.start_date) : null,
    end: filterSetData?.date_range?.end_date && filterSetData.date_range.end_date.toLowerCase() !== 'ongoing'
      ? parseISO(filterSetData.date_range.end_date) : new Date()
  };
  const dashboardType = filterSetData?.dashboard_type || null;
  const deiTargetPillar = filterSetData?.dei_target_pillar || null;

  // Define the context value
  const contextValue: FilterContextType = {
    selectedBrands,
    selectedPlatforms,
    selectedPillars,
    startDate,
    endDate,
    setSelectedBrands,
    setSelectedPlatforms,
    setSelectedPillars,
    setStartDate,
    setEndDate,
    updateUrlParams,
    useDefaultFilters,
    setUseDefaultFilters,
    isInitialized,
    orgName,
    availableBrands,
    allBrands: allBrandsList,
    availablePlatforms,
    availablePillars,
    dataDateRange,
    dashboardType,
    deiTargetPillar,
    brandColors,
    brandColorsLoading,
    refreshBrandColors,
    pillarColors,
    pillarColorsLoading,
    refreshPillarColors
  };

  return (
    <FilterContext.Provider value={contextValue}>
      {children}
    </FilterContext.Provider>
  );
};

export default FilterContextProvider; 